/* Add-to-cart confirmation panel, doubling as the cross-sell surface.
 *
 * Intercepts the theme's product form submit, adds via AJAX, and shows a panel
 * instead of the full-page redirect to /cart that after_add_to_cart "page"
 * would otherwise do.
 *
 * FAIL-SAFE IS THE POINT OF THIS FILE. A broken panel must never produce a dead
 * Add to Cart button, so every path that can throw falls through to the native
 * form POST, which is exactly the behaviour shipped today. Interception is also
 * behind a theme setting so the blast radius can be dialled back without a
 * deploy.
 *
 * Interception happens in the capture phase on document. The theme binds its
 * own jQuery submit handler directly on the form, and a capture-phase listener
 * on an ancestor runs before any listener on the target, so stopping
 * propagation there reliably prevents the theme's AJAX-and-redirect from
 * running. It also means the sticky bar's requestSubmit() flows through here
 * for free.
 */
(function () {
  'use strict';

  var SESSION_PREFIX = 'ccXsell:';
  var FORM_SELECTOR = 'form.product-purchase-form';

  // Mirrors the suffix stripping in snippets/colourway-index.liquid. Longest
  // first, so "Platform High Top" is tried before "High Top".
  var DEFAULT_SUFFIXES = [
    'Platform High Tops', 'Platform High Top', 'Platform Low Top',
    'High Tops', 'High Top', 'Low Tops', 'Low Top',
    'Crossbody Bag', 'Large Tote Bag', 'Tote Bag'
  ];

  var DISCOUNT_TIMEOUT_MS = 1500;
  // Longest we will hold a click-through so the attribution can land.
  var ATTRIBUTION_NAV_MS = 700;

  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  // Applies a discount code to the session without navigating.
  //
  // FAILS OPEN, ALWAYS. Never chain an add-to-cart behind whether this
  // resolved: a discount is a nice-to-have, an add is the whole point. Resolves
  // true/false rather than rejecting so callers cannot accidentally break the
  // add path, and races a timeout so a hanging request cannot stall it either.
  function applyDiscountCode(code) {
    if (!code) return Promise.resolve(false);
    var url = shopRoot() + 'discount/' + encodeURIComponent(code);
    var request = fetch(url, { method: 'GET', credentials: 'same-origin', redirect: 'follow' })
      .then(function (r) { return !!r; })
      .catch(function () { return false; });
    var timeout = new Promise(function (resolve) {
      window.setTimeout(function () { resolve(false); }, DISCOUNT_TIMEOUT_MS);
    });
    return Promise.race([request, timeout]).catch(function () { return false; });
  }

  // First touch wins for _cro_xsell: never overwrite an attribution already on
  // the cart, and never send keys we do not own. /cart/update.js merges
  // attributes, so the theme's shipping-insurance attribute and anything an app
  // writes survive.
  //
  // _cro_xsell_action is separate and DOES upgrade, click -> add, never back.
  // _cro_xsell keeps its original meaning (the shopper engaged with the panel,
  // including a click-through), so the two together separate engagement from
  // conversion without redefining the key already in the order data.
  //
  // knownAttrs lets a caller skip the read. That matters on the click-through
  // path: the card is a link, so the browser starts navigating immediately and
  // a read-then-write round trip loses the race and the attribute is never
  // written. keepalive keeps the write in flight across the navigation.
  function setAttributionOnce(direction, isMatch, action, knownAttrs, beacon) {
    var write = function (attrs) {
      attrs = attrs || {};
      var body = {};
      if (!attrs._cro_xsell) {
        body._cro_xsell = direction;
        body._cro_xsell_match = isMatch ? '1' : '0';
      }
      if (action === 'add' || !attrs._cro_xsell_action) {
        body._cro_xsell_action = action || 'click';
      }
      if (!Object.keys(body).length) return Promise.resolve(attrs);
      var payload = JSON.stringify({ attributes: body });

      // On the click-through path the browser is already navigating. sendBeacon
      // is the only transport the spec guarantees will still be delivered, and
      // it must be called synchronously inside the click handler: a fetch, even
      // with keepalive, can be discarded with the document before it is issued,
      // which is why the attribute was missing on exactly the orders that had
      // the code. Falls back to keepalive fetch where sendBeacon is absent.
      if (beacon) {
        try {
          var sent = navigator.sendBeacon(
            shopRoot() + 'cart/update.js',
            new Blob([payload], { type: 'application/json' })
          );
          if (sent) return Promise.resolve(body);
        } catch (e) {}
      }
      return fetch(shopRoot() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: payload,
        keepalive: true
      })
        .then(function (r) { return r.json(); })
        .then(function (c) { return c.attributes; });
    };
    // Synchronous when the caller already knows the attributes, so the beacon
    // is queued inside the click handler rather than a microtask later.
    if (knownAttrs) { try { return write(knownAttrs); } catch (e) { return Promise.resolve(null); } }
    return fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return write((cart && cart.attributes) || {}); })
      .catch(function () { return null; });
  }

  function colourwayKey(title) {
    if (!title) return null;
    var t = String(title).trim();
    for (var i = 0; i < DEFAULT_SUFFIXES.length; i++) {
      var suffix = DEFAULT_SUFFIXES[i];
      if (t.length > suffix.length && t.slice(-suffix.length) === suffix) {
        return t.slice(0, -suffix.length).trim().toLowerCase();
      }
    }
    return null;
  }

  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function money(cents) {
    try {
      if (window.theme && theme.money_format_with_product_code_preference) {
        // Let the theme format it if it exposes a helper; otherwise fall back.
        if (typeof window.themeMoney === 'function') return window.themeMoney(cents);
      }
    } catch (e) {}
    return '$ ' + (cents / 100).toFixed(2);
  }

  window.customElements.define(
    'cc-add-to-cart-panel',
    class extends HTMLElement {
      connectedCallback() {
        this.mode = this.dataset.mode || 'off';
        if (this.mode !== 'all' && this.mode !== 'qualifying') return;
        if (!window.CCCartClassify) return;

        this.map = window.CCCartClassify.readMap() || {};
        this.copy = readJSON(this.querySelector('[data-atc-copy]')) || {};
        window.CCCartClassify.warnIfTruncated(this.map);

        this.colourway = readJSON(document.getElementById('cc-colourway-index')) || { bags: {}, shoes: {} };

        this.dialog = this.querySelector('.cc-atc-panel__dialog');
        this.xsellWrap = this.querySelector('[data-atc-xsell]');

        // The product rows are reused across opens, so keep the server-rendered
        // markup to restore before each render. Otherwise a prepended colourway
        // match would accumulate every time the panel opened.
        // Shoe cards become inline size-pickers before the rows are
        // snapshotted, so each restore reproduces them. Fail-open: on any
        // throw the cards stay what Liquid rendered, discount-prefixed links.
        try { this.upgradeShoeCards(); } catch (e) {
          console.warn('[atc-panel] size picker upgrade skipped', e);
        }

        this.rowTemplates = {};
        this.querySelectorAll('[data-atc-products]').forEach((row) => {
          this.rowTemplates[row.dataset.atcProducts] = row.innerHTML;
        });
        this.open = false;
        this.direction = null;
        this.busy = false;

        this.bindEvents();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundPreorderClick, true);
        document.removeEventListener('submit', this.boundSubmit, true);
        document.removeEventListener('keydown', this.boundKeydown);
      }

      bindEvents() {
        this.boundSubmit = this.onSubmitCapture.bind(this);
        document.addEventListener('submit', this.boundSubmit, true);

        // Capture phase, so we run before the app's own click handler.
        this.boundPreorderClick = this.onPreorderButtonCapture.bind(this);
        document.addEventListener('click', this.boundPreorderClick, true);

        this.boundKeydown = this.onKeydown.bind(this);
        document.addEventListener('keydown', this.boundKeydown);

        this.querySelectorAll('[data-atc-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );
        this.querySelector('[data-atc-backdrop]').addEventListener('click', () => this.close());

        // Delegated: the colourway match block is built at runtime, so binding
        // to the server-rendered buttons only would miss it.
        this.addEventListener('click', (evt) => {
          var addBtn = evt.target.closest && evt.target.closest('[data-atc-add-variant]');
          if (addBtn && this.contains(addBtn) && !addBtn.disabled) {
            this.addFromBlock(addBtn);
            return;
          }
          // A tap on a not-yet-armed Add lands on the wrapper, because a
          // disabled button swallows no events. Prompt instead of a no-op.
          var wrap = evt.target.closest && evt.target.closest('.cc-atc-block__addwrap');
          if (wrap && this.contains(wrap)) {
            var wrapped = wrap.querySelector('.cc-atc-block__add');
            if (wrapped && wrapped.disabled) {
              this.promptChooseSize(wrap.closest('.cc-atc-block'));
              return;
            }
          }
          // Size-picker interactions are not product clicks.
          if (evt.target.closest && evt.target.closest('select.cc-atc-block__size, .cc-atc-block__note')) return;
          var block = evt.target.closest && evt.target.closest('.cc-atc-block');
          if (block && this.contains(block)) {
            this.track('crosssell_product_click', {
              product_handle: block.dataset.handle || null,
              slot: this.slotOf(block),
              discount_code: this.currentCode() || null
            });
            this.attributeThenNavigate(
              evt, this.directionLabel(), block.dataset.match === 'true'
            );
          }
        });

        // Delegated so the pickers survive the row-template restore and the
        // JS-built match card without rebinding.
        this.addEventListener('change', (evt) => {
          var sel = evt.target.closest && evt.target.closest('select.cc-atc-block__size');
          if (sel && this.contains(sel)) this.onSizeChosen(sel);
        });

        var cta = this.querySelector('[data-atc-xsell-cta]');
        if (cta) {
          cta.addEventListener('click', (evt) => {
            this.track('crosssell_product_click', {
              product_handle: null,
              slot: 'see_all',
              discount_code: this.currentCode() || null
            });
            this.attributeThenNavigate(evt, this.directionLabel(), false);
          });
        }

        var checkout = this.querySelector('[data-atc-checkout]');
        if (checkout) {
          checkout.addEventListener('click', () =>
            this.track('panel_checkout_click', { destination: this.dataset.checkoutUrl || null })
          );
        }
      }

      /* ----- interception ------------------------------------------------- */

      // The app's Pre-Order Now button. We only take it over when we can
      // reproduce its cart line exactly: the campaign must be one we can read
      // out of the app's own config, and the form must carry a variant we
      // recognise. Anything else and we do nothing at all, leaving the app's
      // behaviour untouched, because a pre-order that silently loses its
      // campaign properties is worse than a missing cross-sell.
      onPreorderButtonCapture(evt) {
        var btn = evt.target && evt.target.closest
          ? evt.target.closest('[data-essential-preorder-element="button"]')
          : null;
        if (!btn) return;
        try {
          var form = btn.closest(FORM_SELECTOR);
          if (!form || form.dataset.ccBypass === '1') return;
          var idInput = form.querySelector('[name="id"]');
          if (!idInput || !idInput.value) return;

          // Must be able to reproduce the app's own line-item properties.
          var pre = this.preorderFor(idInput.value, null);
          if (!pre) return;

          evt.preventDefault();
          evt.stopPropagation();
          this.handleAdd(form, pre.props);
        } catch (e) {
          // Do NOT failSafe-resubmit here: the app's own handler is still
          // bound and will run as normal once we return without preventing.
          console.warn('[atc-panel] pre-order intercept skipped, app handles it', e);
        }
      }

      onSubmitCapture(evt) {
        var form = evt.target;
        if (!form || !form.matches || !form.matches(FORM_SELECTOR)) return;
        if (form.dataset.ccBypass === '1') return; // fail-safe resubmit

        try {
          var idInput = form.querySelector('[name="id"]');
          if (!idInput || !idInput.value) return; // let the theme handle validation

          evt.preventDefault();
          evt.stopPropagation();
          this.handleAdd(form);
        } catch (e) {
          this.failSafe(form, e);
        }
      }

      // Native submit, bypassing every listener including this one.
      //
      // The error is reported BEFORE the redirect, because the redirect ends
      // this JS context. Without that, a broken panel is indistinguishable from
      // a working site: the shopper still reaches the cart and nothing surfaces.
      failSafe(form, err) {
        if (err) {
          console.error('[atc-panel] falling back to the normal form post', err);
          try {
            this.track('panel_error', {
              message: String((err && err.message) || err),
              stack: String((err && err.stack) || '').split('\n').slice(0, 3).join(' | '),
              panel_version: this.dataset.ccPanelVersion || null
            });
          } catch (e) {
            /* reporting must never be the thing that breaks the fallback */
          }
        }
        try {
          form.dataset.ccBypass = '1';
          form.submit();
        } catch (e) {
          window.location = this.dataset.cartUrl || '/cart';
        }
      }

      // preorderProps, when present, are the line-item properties the Essential
      // app's own button would have posted. Sent as FormData properties[...] so
      // the cart line is byte-identical to a PDP pre-order.
      handleAdd(form, preorderProps) {
        if (this.busy) return;
        this.busy = true;

        var button =
          form.querySelector('[data-essential-preorder-element="button"]') ||
          form.querySelector('button[type="submit"]');
        var previous = button ? button.innerHTML : null;
        if (button) {
          button.setAttribute('disabled', 'disabled');
          button.innerHTML = '<div class="loading-spinner loading-spinner--in-btn"></div>';
        }

        var restore = () => {
          this.busy = false;
          if (button) {
            button.removeAttribute('disabled');
            if (previous !== null) button.innerHTML = previous;
          }
        };

        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

        var body = new FormData(form);
        if (preorderProps) {
          Object.keys(preorderProps).forEach((k) => {
            // Only add what the form is not already carrying, so the app's own
            // hidden inputs always win.
            if (!body.has('properties[' + k + ']')) {
              body.append('properties[' + k + ']', preorderProps[k]);
            }
          });
        }

        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: body
        })
          .then((r) => {
            if (!r.ok) throw new Error('add failed: ' + r.status);
            return r.json();
          })
          .then((added) => this.afterAdd(added))
          .then(() => restore())
          .catch((e) => {
            restore();
            this.failSafe(form, e);
          });
      }

      afterAdd(added) {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            this.refreshThemeCart();
            this.readCartCodes(cart);
            this.cartAttrs = (cart && cart.attributes) || {};

            var result = window.CCCartClassify.classify(cart, this.map);
            var qualifies = this.resolveOffer(result, cart);

            if (!qualifies && this.mode === 'qualifying') {
              // Setting says: only interrupt for a cross-sell. Otherwise do
              // exactly what the theme does today.
              window.location = this.dataset.cartUrl || '/cart';
              return;
            }

            this.render(added, cart, qualifies);
            this.show();
          });
      }

      // Returns the direction to offer, or null for a plain confirmation.
      resolveOffer(result, cart) {
        if (!result || !result.direction) return null;

        var isB2S = result.direction === 'bagsToShoes';
        var key = isB2S ? 'b2s' : 's2b';
        if (this.dataset[isB2S ? 'bagsToShoes' : 'shoesToBags'] !== 'true') return null;
        if (this.hasSeen(result.direction)) return null;

        var min = Number(this.dataset[key + 'Min'] || 0);
        if (min > 0 && cart.total_price < min) return null;

        // Only offer if there is at least one product block to show.
        var blocks = this.querySelector('[data-atc-products="' + result.direction + '"]');
        if (!blocks || !blocks.children.length) return null;

        return result.direction;
      }

      /* ----- rendering ----------------------------------------------------- */

      render(added, cart, direction) {
        var img = this.querySelector('[data-atc-image]');
        var title = this.querySelector('[data-atc-title]');
        var meta = this.querySelector('[data-atc-meta]');

        if (img) {
          img.innerHTML = added && added.image
            ? '<img src="' + added.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') +
              '" alt="" width="56" height="56">'
            : '';
        }
        if (title) title.textContent = (added && added.product_title) || '';
        if (meta) {
          var bits = [];
          if (added && added.variant_title && added.variant_title !== 'Default Title') {
            bits.push(added.variant_title);
          }
          bits.push(this.cartCountLabel(cart));
          meta.textContent = bits.join(' · ');
        }

        this.direction = direction;
        this.querySelectorAll('[data-atc-products]').forEach((el) => {
          // Restore the server-rendered picks before every render so a match
          // from a previous open cannot linger or stack.
          if (this.rowTemplates[el.dataset.atcProducts] != null) {
            el.innerHTML = this.rowTemplates[el.dataset.atcProducts];
          }
          el.hidden = el.dataset.atcProducts !== direction;
        });

        var ctaEl = this.querySelector('[data-atc-xsell-cta]');

        if (!direction) {
          this.xsellWrap.hidden = true;
          // The category CTA sits in the footer now, so hiding the cross-sell
          // block no longer hides it. In confirmation_only there is no offer,
          // so Check out is the only action.
          if (ctaEl) ctaEl.hidden = true;
          this.match = null;
          return;
        }

        var copy = this.copy[direction] || {};
        this.match = this.applyColourwayMatch(added, direction, copy);

        // Heading AND body both switch on the match. With a match they name the
        // one product being shown; without one they lead with the offer, since
        // there is no specific product to name.
        var heading = copy.heading;
        var body = copy.body;
        if (this.match) {
          if (copy.matchHeading) heading = copy.matchHeading.replace('[name]', this.match.name);
          if (copy.matchBody) body = copy.matchBody.replace('[name]', this.match.name);
        }
        this.setText('[data-atc-xsell-heading]', heading);
        this.setText('[data-atc-xsell-body]', body);
        // An empty body setting means no body line, not an empty gap.
        var bodyEl = this.querySelector('[data-atc-xsell-body]');
        if (bodyEl) bodyEl.hidden = !body;

        // The pill is off by default and is not rendered at all in that case,
        // so this is a no-op unless it has been switched back on.
        var badge = this.querySelector('[data-atc-badge]');
        if (badge) {
          badge.textContent = copy.badge || '';
          badge.hidden = !copy.badge;
        }

        // The offer line is identical with or without a match on purpose: it is
        // the only line saying the discount covers the whole category rather
        // than just the product on screen.
        var offer = this.querySelector('[data-atc-offer]');
        if (offer) {
          var offerText = this.offerLineFor(copy);
          offer.textContent = offerText || '';
          offer.hidden = !offerText;
        }

        var cta = ctaEl;
        if (cta) {
          cta.hidden = !copy.button;
          cta.textContent = copy.button || '';
          // The discount-aware URL is /discount/CODE?redirect=..., falling back
          // to the plain collection when this direction has no live offer.
          var isB2SDir = direction === 'bagsToShoes';
          cta.setAttribute(
            'href',
            this.dataset[isB2SDir ? 'b2sCtaUrl' : 's2bCtaUrl'] ||
              this.dataset[isB2SDir ? 'b2sUrl' : 's2bUrl'] ||
              '#'
          );
        }

        // After the row is final, so the JS-built match card is covered too.
        // Fail-open: a throw here must not cost the shopper the panel.
        try {
          this.applyPreorderToAddableCards(
            this.querySelector('[data-atc-products="' + direction + '"]')
          );
        } catch (e) {
          console.warn('[atc-panel] bag pre-order pass skipped', e);
        }

        this.xsellWrap.hidden = false;
      }

      /* ----- colourway match -------------------------------------------- */

      // Leads the row with the counterpart of what was just added, when one
      // exists and is purchasable. Falls through silently otherwise: never an
      // empty slot, never a broken match.
      applyColourwayMatch(added, direction, copy) {
        try {
          var key = colourwayKey(added && added.product_title);
          if (!key) return null;

          var category = copy.matchCategory === 'shoe' ? 'shoes' : 'bags';
          var entry = this.colourway[category] && this.colourway[category][key];
          if (!entry) return null;

          var row = this.querySelector('[data-atc-products="' + direction + '"]');
          if (!row) return null;

          // Do not recommend the thing they just added.
          if (added && added.handle && entry.h === added.handle) return null;

          var block = this.buildMatchBlock(entry, copy.matchCategory);
          if (!block) return null;

          // Drop a duplicate of the match from the manual picks, then lead with it.
          row.querySelectorAll('[data-handle="' + entry.h + '"]').forEach((el) => el.remove());
          row.insertBefore(block, row.firstElementChild);

          // Keep the row at three so the layout does not grow.
          while (row.children.length > 3) row.removeChild(row.lastElementChild);

          return entry;
        } catch (e) {
          console.warn('[atc-panel] colourway match skipped', e);
          return null;
        }
      }

      buildMatchBlock(entry, category) {
        var addable = category === 'bag' && entry.variants === 1 && entry.v;
        // A shoe with embedded variant data gets the same inline size picker
        // as the Liquid cards, from the same builder. Without that data (a
        // cached page rendered before this build) it falls through to the
        // discount-prefixed link card.
        var sized = category === 'shoe' && entry.vs && entry.vs.length;
        if (sized) return this.buildSizedMatchBlock(entry);

        var el = document.createElement(addable ? 'div' : 'a');
        el.className = 'cc-atc-block' + (addable ? ' cc-atc-block--addable' : '');
        el.dataset.handle = entry.h;
        el.dataset.match = 'true';
        if (addable && entry.vs) el.setAttribute('data-variants', JSON.stringify(entry.vs));
        var href = this.discountUrl(entry.url);
        if (!addable) el.setAttribute('href', href);

        var inner = addable ? document.createElement('a') : document.createDocumentFragment();
        if (addable) {
          inner.className = 'cc-atc-block__link';
          inner.setAttribute('href', href);
        }

        var media = document.createElement('span');
        media.className = 'cc-atc-block__media';
        if (entry.img) {
          var img = document.createElement('img');
          img.setAttribute('src', entry.img);
          img.setAttribute('alt', '');
          img.setAttribute('width', '120');
          img.setAttribute('height', '150');
          img.setAttribute('loading', 'lazy');
          media.appendChild(img);
        }

        var title = document.createElement('span');
        title.className = 'cc-atc-block__title';
        title.textContent = entry.t;

        var price = this.buildPrice(entry);

        inner.appendChild(media);
        inner.appendChild(title);
        inner.appendChild(price);

        if (addable) {
          el.appendChild(inner);
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'button button--small cc-atc-block__add';
          btn.dataset.atcAddVariant = String(entry.v);
          btn.dataset.addedLabel = this.addedLabel();
          btn.dataset.ccIdleLabel = this.addLabel();
          btn.textContent = btn.dataset.ccIdleLabel;
          el.appendChild(btn);
          var bagNote = document.createElement('span');
          bagNote.className = 'cc-atc-block__note';
          bagNote.hidden = true;
          el.appendChild(bagNote);
        } else {
          el.appendChild(inner);
          var cta = document.createElement('span');
          cta.className = 'cc-atc-block__cta';
          cta.textContent = this.chooseLabel();
          el.appendChild(cta);
        }
        return el;
      }

      buildSizedMatchBlock(entry) {
        var el = document.createElement('div');
        el.className = 'cc-atc-block cc-atc-block--addable cc-atc-block--sized';
        el.dataset.handle = entry.h;
        el.dataset.match = 'true';
        el.setAttribute('data-variants', JSON.stringify(entry.vs));

        var link = document.createElement('a');
        link.className = 'cc-atc-block__link';
        link.setAttribute('href', this.discountUrl(entry.url));

        var media = document.createElement('span');
        media.className = 'cc-atc-block__media';
        if (entry.img) {
          var img = document.createElement('img');
          img.setAttribute('src', entry.img);
          img.setAttribute('alt', '');
          img.setAttribute('width', '120');
          img.setAttribute('height', '150');
          img.setAttribute('loading', 'lazy');
          media.appendChild(img);
        }
        var title = document.createElement('span');
        title.className = 'cc-atc-block__title';
        title.textContent = entry.t;
        var price = this.buildPrice(entry);

        link.appendChild(media);
        link.appendChild(title);
        link.appendChild(price);
        el.appendChild(link);
        this.appendSizePicker(el, entry.vs);
        return el;
      }

      // Price, plus the struck-through compare-at price when the product is on
      // sale, matching the Liquid cards and the theme's product grid.
      buildPrice(entry) {
        var price = document.createElement('span');
        price.className = 'cc-atc-block__price';
        var now = document.createElement('span');
        now.className = 'theme-money';
        now.textContent = entry.p;
        price.appendChild(now);
        if (entry.cp) {
          price.appendChild(document.createTextNode(' '));
          var was = document.createElement('s');
          was.className = 'cc-atc-block__compare theme-money';
          was.textContent = entry.cp;
          price.appendChild(was);
        }
        return price;
      }

      // Labels are lifted off the server-rendered blocks so they stay in the
      // shop's locale without duplicating strings in JS.
      addLabel() {
        var b = this.querySelector('[data-atc-add-variant]');
        return (b && b.dataset.ccAdded !== '1' && b.textContent.trim()) || 'Add to Cart';
      }
      addedLabel() {
        var b = this.querySelector('[data-atc-add-variant]');
        return (b && b.dataset.addedLabel) || 'Added';
      }
      chooseLabel() {
        var c = this.querySelector('.cc-atc-block__cta');
        return (c && c.textContent.trim()) || 'Choose options';
      }

      // The live code for the current direction, or empty when this direction
      // has no offer (master switch off, line off, or code setting blank).
      currentCode() {
        if (!this.direction) return '';
        return this.dataset[this.direction === 'bagsToShoes' ? 'b2sCode' : 's2bCode'] || '';
      }

      // Wraps a product URL so the click both applies the code and lands on the
      // PDP. The Liquid-rendered cards build this server-side; anything built
      // here has to do the same or the shopper arrives with no code on the
      // session and the offer we just promised them silently does not exist.
      // Falls back to the bare URL when this direction has no live code.
      discountUrl(url) {
        var code = this.currentCode();
        if (!code || !url) return url;
        return shopRoot() + 'discount/' + encodeURIComponent(code) + '?redirect=' + url;
      }

      slotOf(block) {
        var row = block.parentElement;
        if (!row) return null;
        return [].indexOf.call(row.children, block) + 1;
      }

      // Resolves which offer line to show.
      //
      // Both codes are now configured in Admin to combine with order and product
      // discounts, so a shopper's own club or family code no longer displaces
      // ours and the standard line is honest for everyone. The override is left
      // in place but defaults off: detection is wired and reported on
      // panel_shown, so if that data ever shows a case the standard line
      // overstates, enabling it with text swaps the line and enabling it with
      // empty text suppresses it.
      offerLineFor(copy) {
        var override = this.copy && this.copy.existingCodeOverride;
        if (override && override.enabled && this.hasExistingDiscountCode()) {
          return override.offer || '';
        }
        return copy.offer || '';
      }

      // Records which codes the cart already carries, so the panel can report
      // whether a shopper arrived with one of their own. Tolerant of shape:
      // the Ajax cart has returned discount_codes as objects and, on some
      // versions, as bare strings, and an absent key is normal on an empty cart.
      readCartCodes(cart) {
        try {
          var raw = (cart && cart.discount_codes) || [];
          this.cartCodes = raw
            .map((d) => (typeof d === 'string' ? d : d && d.code) || '')
            .filter(Boolean)
            .map((c) => c.toUpperCase());
        } catch (e) {
          this.cartCodes = [];
        }
      }

      // Our own codes, both directions. A shopper who took the bags offer and
      // then triggers the shoes panel is carrying OUR code, not their own, and
      // must not be counted as an existing-code case.
      ourCodes() {
        return [this.dataset.b2sCode, this.dataset.s2bCode]
          .filter(Boolean)
          .map((c) => c.toUpperCase());
      }

      // True when the cart carries a code that is not one of ours. Wired now so
      // the segment is measurable on panel_shown; it no longer gates anything,
      // because both codes are set to combine with order and product discounts.
      hasExistingDiscountCode() {
        var ours = this.ourCodes();
        return (this.cartCodes || []).some((c) => ours.indexOf(c) === -1);
      }

      setText(sel, value) {
        var el = this.querySelector(sel);
        if (el) el.textContent = value || '';
      }

      cartCountLabel(cart) {
        var n = cart.item_count;
        this.lastCartCount = n;
        return n === 1 ? '1 item in cart' : n + ' items in cart';
      }

      /* ----- pre-order state on single-variant (bag) cards ------------------ */

      // Bags are single-variant, so there is no size control: the whole card is
      // either a pre-order or it is not. Same source of truth as shoes, the
      // app's own campaign config, so the panel and the PDP cannot disagree.
      // A bag with no campaign is left exactly as Liquid rendered it.
      applyPreorderToAddableCards(row) {
        if (!row) return;
        row.querySelectorAll('.cc-atc-block--addable').forEach((block) => {
          if (block.classList.contains('cc-atc-block--sized')) return; // shoes handle their own
          var btn = block.querySelector('.cc-atc-block__add[data-atc-add-variant]');
          if (!btn) return;
          var note = block.querySelector('.cc-atc-block__note');
          var v = null;
          try {
            var vs = JSON.parse(block.getAttribute('data-variants') || '[]');
            v = vs.filter((x) => String(x[0]) === String(btn.dataset.atcAddVariant))[0] || null;
          } catch (e) {}
          if (!btn.dataset.ccIdleLabel) btn.dataset.ccIdleLabel = btn.textContent.trim();
          this.applyPreorderState(btn, note, v);
        });
      }

      /* ----- inline size selection for shoe cards -------------------------- */

      sizeCopy() {
        var c = (this.copy && this.copy.sizes) || {};
        return {
          choose: c.choose || 'Choose size',
          chooseFirst: c.chooseFirst || 'Choose a size first',
          soldOut: c.soldOut || 'That size just sold out'
        };
      }

      // Converts the Liquid-rendered b2s link cards into size-picking cards.
      // The SAME appendSizePicker builds the slot-1 match card's control, so
      // there is exactly one code path for shoe cards; that split is what
      // produced the last two bugs.
      upgradeShoeCards() {
        this.querySelectorAll('[data-atc-products="bagsToShoes"] a.cc-atc-block[data-variants]').forEach((a) => {
          var vs;
          try { vs = JSON.parse(a.getAttribute('data-variants')); } catch (e) { return; }
          if (!vs || !vs.length) return;

          var div = document.createElement('div');
          div.className = 'cc-atc-block cc-atc-block--addable cc-atc-block--sized';
          div.dataset.handle = a.dataset.handle || '';
          div.setAttribute('data-variants', a.getAttribute('data-variants'));

          // The PDP stays reachable through the media and title, and keeps the
          // discount prefix the card link carried.
          var link = document.createElement('a');
          link.className = 'cc-atc-block__link';
          link.setAttribute('href', a.getAttribute('href') || '#');
          ['media', 'title', 'price'].forEach((part) => {
            var el = a.querySelector('.cc-atc-block__' + part);
            if (el) link.appendChild(el);
          });
          div.appendChild(link);
          this.appendSizePicker(div, vs);
          a.replaceWith(div);
        });
      }

      // Builds the size control onto a card. Sixteen dual-labelled sizes make
      // chips impossible on a 150px card, so this is a native select: the OS
      // spinner gives full-width labels, free disabled-option handling and a
      // 44px target without costing panel height.
      appendSizePicker(block, vs) {
        var copy = this.sizeCopy();
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'button button--small cc-atc-block__add';
        btn.dataset.addedLabel = this.addedLabel();
        btn.dataset.ccIdleLabel = this.addLabel();
        btn.textContent = btn.dataset.ccIdleLabel;

        var note = document.createElement('span');
        note.className = 'cc-atc-block__note';
        note.hidden = true;

        if (vs.length === 1) {
          // Single variant behaves like a bag: no size control, straight add.
          var only = vs[0];
          if (only[2] === 1) {
            btn.dataset.atcAddVariant = String(only[0]);
            this.applyPreorderState(btn, note, only);
          } else {
            btn.setAttribute('disabled', 'disabled');
          }
        } else {
          var sel = document.createElement('select');
          sel.className = 'cc-atc-block__size';
          sel.setAttribute('aria-label', copy.choose);
          // Selectable on purpose: a disabled placeholder makes some
          // renderers display the first enabled size in the closed control,
          // which reads as a selection that was never made. Picking it again
          // simply disarms the Add button.
          var ph = document.createElement('option');
          ph.value = '';
          ph.textContent = copy.choose;
          ph.selected = true;
          sel.appendChild(ph);
          vs.forEach((v) => {
            var o = document.createElement('option');
            o.value = String(v[0]);
            // The option value exactly as Shopify stores it, no abbreviation.
            o.textContent = v[1];
            if (v[2] !== 1) o.disabled = true;
            sel.appendChild(o);
          });
          block.appendChild(sel);
          btn.setAttribute('disabled', 'disabled');
        }

        block.appendChild(note);
        var wrap = document.createElement('span');
        wrap.className = 'cc-atc-block__addwrap';
        wrap.appendChild(btn);
        block.appendChild(wrap);
      }

      onSizeChosen(sel) {
        var block = sel.closest('.cc-atc-block');
        var btn = block && block.querySelector('.cc-atc-block__add');
        var note = block && block.querySelector('.cc-atc-block__note');
        if (!btn) return;
        var vid = sel.value;
        if (!vid) {
          btn.setAttribute('disabled', 'disabled');
          return;
        }
        var vs = [];
        try { vs = JSON.parse(block.getAttribute('data-variants') || '[]'); } catch (e) {}
        var v = vs.filter((x) => String(x[0]) === String(vid))[0];
        btn.dataset.atcAddVariant = String(vid);
        btn.removeAttribute('disabled');
        if (note) { note.hidden = true; note.classList.remove('cc-atc-block__note--error'); }
        this.applyPreorderState(btn, note, v);
      }

      // Mirrors the Essential Pre-order app's PDP treatment for one variant:
      // button label, colours and message all come from the app's own config,
      // so the two surfaces cannot disagree.
      applyPreorderState(btn, note, v) {
        var pre = v ? this.preorderFor(v[0], v) : null;
        if (pre) {
          btn.textContent = pre.buttonText;
          btn.classList.add('cc-atc-block__add--preorder');
          if (pre.bg) { btn.style.backgroundColor = pre.bg; btn.style.borderColor = pre.bg; }
          btn.dataset.atcProps = JSON.stringify(pre.props);
          if (note && pre.message) {
            note.textContent = pre.message;
            note.style.color = pre.msgColor;
            note.hidden = false;
          }
        } else {
          btn.textContent = btn.dataset.ccIdleLabel || this.addLabel();
          btn.classList.remove('cc-atc-block__add--preorder');
          btn.style.backgroundColor = '';
          btn.style.borderColor = '';
          delete btn.dataset.atcProps;
          if (note) { note.style.color = ''; }
        }
      }

      // The condition the live PDP actually uses, read from the app's own
      // embed config (window.essentialPreorderConfigs, inlined on every page):
      // the variant is placed in an OUT_OF_STOCK campaign, is available, and
      // its inventory is at or below zero. Quantity comes from the app's map
      // first, falling back to the Liquid snapshot. When the app is disabled
      // the config is absent, this returns null everywhere, and both surfaces
      // show a plain Add to Cart, still in agreement.
      preorderFor(variantId, v) {
        try {
          var cfg = window.essentialPreorderConfigs;
          if (!cfg || !cfg.preorderCampaigns) return null;
          if (!this._preCampaigns) {
            var map = {};
            cfg.preorderCampaigns.forEach((c) => {
              ((c.placement && c.placement.specificProducts) || []).forEach((pp) => {
                // The app has shipped both shapes: objects with a gid ({id:
                // "gid://shopify/ProductVariant/42921812230210"}) until about
                // 2026-09-21, bare numbers ([42921812230210]) since. Reading
                // only pp.id silently emptied this map and every pre-order
                // item added from the panel went in untagged.
                var raw = pp && typeof pp === 'object' ? pp.id : pp;
                var id = String(raw == null ? '' : raw).split('/').pop();
                if (id) map[id] = c;
              });
            });
            this._preCampaigns = map;
          }
          var c = this._preCampaigns[String(variantId)];
          if (!c || !c.content || c.content.preorderType !== 'OUT_OF_STOCK') return null;
          if (v && v[2] !== 1) return null; // greyed-out wins over pre-order
          var qty = cfg.inventoryQuantityByVariantId && cfg.inventoryQuantityByVariantId[String(variantId)];
          if (typeof qty !== 'number') qty = v ? Number(v[3]) : 0;
          if (qty > 0) return null;
          // The exact properties the app's own Pre-Order Now button posts, so
          // a panel add produces the same cart line as a PDP add.
          var props = { _essential_preorder_campaign_id: c.id };
          if (c.content.cartNoteKey && c.content.cartNoteText) {
            props[c.content.cartNoteKey] = c.content.cartNoteText;
          }
          return {
            buttonText: c.content.buttonText || 'Pre-Order Now',
            message: c.content.message || '',
            bg: c.design && c.design.monochromeBackgroundColor,
            msgColor: (c.design && c.design.messageColor) || '#ff0000',
            props: props
          };
        } catch (e) {
          return null;
        }
      }

      promptChooseSize(block) {
        if (!block || !block.querySelector('select.cc-atc-block__size')) return;
        var copy = this.sizeCopy();
        var note = block.querySelector('.cc-atc-block__note');
        if (note) {
          note.textContent = copy.chooseFirst;
          note.classList.add('cc-atc-block__note--error');
          note.style.color = '';
          note.hidden = false;
        }
        var sel = block.querySelector('select.cc-atc-block__size');
        if (sel) sel.focus();
      }

      // A 422 from /cart/add.js is a real outcome on shoes: their inventory
      // policy is DENY, and the embedded availability is a snapshot that can
      // go stale when another shopper takes the last pair. Mark the size, keep
      // the panel open, and let them pick again.
      handleSoldOut(button, block, variantId, err) {
        var copy = this.sizeCopy();
        var sel = block && block.querySelector('select.cc-atc-block__size');
        if (sel) {
          var opt = sel.querySelector('option[value="' + variantId + '"]');
          if (opt) opt.disabled = true;
          sel.value = '';
        }
        try {
          var vs = JSON.parse(block.getAttribute('data-variants') || '[]');
          vs.forEach((x) => { if (String(x[0]) === String(variantId)) x[2] = 0; });
          block.setAttribute('data-variants', JSON.stringify(vs));
        } catch (e) {}
        button.setAttribute('disabled', 'disabled');
        delete button.dataset.atcAddVariant;
        this.applyPreorderState(button, null, null);
        var note = block && block.querySelector('.cc-atc-block__note');
        if (note) {
          note.textContent = copy.soldOut;
          note.classList.add('cc-atc-block__note--error');
          note.style.color = '';
          note.hidden = false;
        }
        this.track('crosssell_add_failed', {
          product_handle: (block && block.dataset.handle) || null,
          variant_id: Number(variantId),
          error: (err && err.body && (err.body.description || err.body.message)) || ('status ' + (err && err.status))
        });
      }

      /* ----- instant add from a product block ------------------------------ */

      addFromBlock(button) {
        // Guard against a rapid double tap adding twice.
        if (button.dataset.ccAdding === '1' || button.dataset.ccAdded === '1') return;
        button.dataset.ccAdding = '1';

        var variantId = button.dataset.atcAddVariant;
        var previous = button.textContent;
        button.setAttribute('disabled', 'disabled');

        var root = shopRoot();
        var block = button.closest('.cc-atc-block');
        var isMatch = !!(block && block.dataset.match === 'true');
        var code = this.currentCode();

        // Discount first so the code is on the session before the line lands,
        // but the add is NOT conditional on it: applyDiscountCode resolves
        // false rather than rejecting, so a dead discount endpoint still adds.
        //
        // The extra try/catch covers the case where the discount call throws
        // synchronously rather than rejecting, which an interfering app script
        // can cause. Without it the button would sit disabled and nothing would
        // be added, which is exactly the outcome fail-open exists to prevent.
        var discountStep;
        try {
          discountStep = applyDiscountCode(code);
        } catch (e) {
          console.error('[atc-panel] discount call threw, adding anyway', e);
          discountStep = Promise.resolve(false);
        }

        // Pre-order adds carry the same line-item properties the app's own
        // PDP button posts, set on the button when the size was chosen.
        var item = { id: Number(variantId), quantity: 1 };
        var props = null;
        try { props = button.dataset.atcProps ? JSON.parse(button.dataset.atcProps) : null; } catch (e) {}
        if (props) item.properties = props;

        discountStep
          .then(() =>
            fetch(root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [item] })
            })
          )
          .then((r) => {
            if (r.ok) return r.json();
            return r.json().catch(() => ({})).then((body) => {
              var err = new Error('block add failed: ' + r.status);
              err.status = r.status;
              err.body = body;
              throw err;
            });
          })
          .then(() => setAttributionOnce(this.directionLabel(), isMatch, 'add'))
          .then(() => fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }))
          .then((r) => r.json())
          .then((cart) => {
            // Swap to an added state in place. The panel stays open and nothing
            // navigates, which is the whole point of instant add.
            button.dataset.ccAdded = '1';
            button.textContent = button.dataset.addedLabel || 'Added';
            button.classList.add('cc-atc-block__add--added');
            var meta = this.querySelector('[data-atc-meta]');
            if (meta) {
              var parts = meta.textContent.split(' · ');
              parts[parts.length - 1] = this.cartCountLabel(cart);
              meta.textContent = parts.join(' · ');
            }
            this.refreshThemeCart();
            this.track('crosssell_instant_add', {
              product_handle: block ? block.dataset.handle || null : null,
              slot: this.slotOf(block),
              variant_id: Number(variantId),
              preorder: !!(props && props._essential_preorder_campaign_id),
              discount_code: code || null,
              cart_discounts: (cart.discount_codes || []).map((d) => d.code).join(',') || null
            });
          })
          .catch((e) => {
            if (e && e.status === 422 && block) {
              // Stale snapshot: the variant sold out under a DENY policy since
              // the page rendered. Recoverable, so keep the panel open.
              this.handleSoldOut(button, block, variantId, e);
              return;
            }
            console.warn('[atc-panel] block add failed', e);
            button.removeAttribute('disabled');
            button.textContent = previous;
          })
          .finally(() => {
            button.dataset.ccAdding = '0';
          });
      }

      // The card is a link, so the browser starts tearing the page down the
      // moment the click returns. Fire-and-forget writes lose that race, which
      // is what produced orders carrying our code with no _cro_xsell: the code
      // rides in the URL and survives, the attribute did not.
      //
      // So hold the navigation for a bounded moment instead of betting on
      // delivery. If the write has not settled in ATTRIBUTION_NAV_MS we
      // navigate anyway, and any throw navigates immediately: the shopper's
      // journey is never blocked for the sake of a measurement.
      attributeThenNavigate(evt, direction, isMatch) {
        var anchor = evt.target && evt.target.closest ? evt.target.closest('a[href]') : null;
        var href = anchor && anchor.getAttribute('href');
        var holdable = !!href && href !== '#' && !evt.defaultPrevented && !evt.metaKey &&
          !evt.ctrlKey && !evt.shiftKey && !(anchor && anchor.target === '_blank');
        try {
          // When we can hold the navigation we want a promise that settles on
          // DELIVERY, so use the ordinary request: sendBeacon only promises to
          // queue, and resolving on the queue released the hold immediately and
          // put us straight back in the race we were trying to remove. The
          // beacon is still the right transport when we cannot hold, such as a
          // modified click opening a new tab.
          var write = setAttributionOnce(direction, isMatch, 'click', this.cartAttrs || {}, !holdable);
          if (!holdable) return;
          evt.preventDefault();
          var went = false;
          var go = function () {
            if (went) return;
            went = true;
            window.location.assign(href);
          };
          window.setTimeout(go, ATTRIBUTION_NAV_MS);
          Promise.resolve(write).then(go, go);
        } catch (e) {
          console.warn('[atc-panel] attribution skipped, navigating', e);
          if (href && href !== '#') window.location.assign(href);
        }
      }

      // Let the theme update its own cart count and any bound areas.
      refreshThemeCart() {
        try {
          document.documentElement.dispatchEvent(
            new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
          );
        } catch (e) {}
      }

      /* ----- open / close -------------------------------------------------- */

      show() {
        this.previouslyFocused = document.activeElement;
        this.hidden = false;
        void this.offsetHeight;
        this.classList.add('cc-atc-panel--open');
        this.open = true;

        // Fires on EVERY open, confirmation_only included. This is the health
        // signal: if panel_shown goes to zero the feature is dead, whether or
        // not anything threw.
        this.track('panel_shown', {
          cart_item_count: this.lastCartCount,
          discount_code: this.currentCode() || null,
          // Sizes the segment arriving with a code of their own. Both codes now
          // combine, so this is measurement, not a reason to suppress anything.
          existing_code: this.hasExistingDiscountCode(),
          existing_codes: (this.cartCodes || []).join(',') || null,
          panel_version: this.dataset.ccPanelVersion || null
        });
        if (this.direction) this.markSeen();

        var first = this.querySelector('[data-atc-close]');
        if (first) first.focus();
      }

      close() {
        if (!this.open) return;
        this.open = false;
        this.classList.remove('cc-atc-panel--open');
        if (this.direction) {
          this.track('crosssell_dismissed');
          this.markSeen();
        }
        window.setTimeout(() => {
          this.hidden = true;
        }, 220);

        if (this.previouslyFocused && this.previouslyFocused.focus) {
          this.previouslyFocused.focus();
        }
      }

      onKeydown(evt) {
        if (!this.open) return;

        if (evt.key === 'Escape') {
          this.close();
          return;
        }
        if (evt.key !== 'Tab') return;

        var focusable = [...this.dialog.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )].filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;

        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (evt.shiftKey && document.activeElement === first) {
          evt.preventDefault();
          last.focus();
        } else if (!evt.shiftKey && document.activeElement === last) {
          evt.preventDefault();
          first.focus();
        }
      }

      /* ----- frequency cap -------------------------------------------------- */

      // Only the cross-sell content is capped. The plain confirmation is not,
      // so it shows on every add.
      hasSeen(direction) {
        try {
          return window.sessionStorage.getItem(SESSION_PREFIX + direction) === '1';
        } catch (e) {
          return false;
        }
      }

      markSeen() {
        if (!this.direction) return;
        try {
          window.sessionStorage.setItem(SESSION_PREFIX + this.direction, '1');
        } catch (e) {
          /* private mode: simply not capped */
        }
      }

      /* ----- analytics ------------------------------------------------------ */

      // One CustomEvent on document, matching the theme's own convention, with
      // the event name in detail.event so a single listener can catch all five.
      // dataLayer is only touched if GTM already put one on the page. No
      // tracking library is added.
      track(eventName, payload) {
        var detail = Object.assign(
          {
            event: eventName,
            direction: this.directionLabel(),
            colourway_match: !!this.match,
            colourway: this.match ? this.match.name : null
          },
          payload || {}
        );

        document.dispatchEvent(
          new CustomEvent('theme:add-to-cart-panel', {
            bubbles: true,
            cancelable: false,
            detail: detail
          })
        );

        if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
      }

      directionLabel() {
        if (this.direction === 'bagsToShoes') return 'bags_to_shoes';
        if (this.direction === 'shoesToBags') return 'shoes_to_bags';
        return 'confirmation_only';
      }
    }
  );
})();
